<script setup lang="ts">
import type Tech from '@/types/Tech.ts'
import { computed, onMounted, ref } from 'vue'
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { CDN } from '@/models/cdn.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Backend from '@/models/backend.ts'
import { useRouter } from 'vue-router'

/**
 * Stores / models
 */
const gameStore = useGameStore()
const assetStore = useAssetStore()
const allianceStore = useAllianceStore()
const router = useRouter()

/**
 * Props
 */
const props = defineProps<{
  tech: Tech
}>()

/**
 * Variables
 */
const top = ref(0)
const left = ref(0)

/**
 * Functions
 */
const selectPrototype = async () => {
  try {
    // Set alliance prototype via API call
    const backend = new Backend()
    await backend.setAlliancePrototype(props.tech.id)

    gameStore.setTinySuccessToastMessage(`Selected ${name.value} as alliance prototype`)

    // Get the return route or default to alliance research
    const returnRoute = gameStore.alliancePrototypeReturnRoute || '/diplomacy/alliance/research'

    // Reset the flag and return to the stored route
    gameStore.resetAlliancePrototypeSelection()
    await router.push(returnRoute)
  } catch (error) {
    gameStore.setTinyErrorToastMessage('Failed to select prototype')
  }
}

/**
 * Computed
 */
const name = computed(() => props.tech.name)

const provideIcons = computed<string[]>(() => {
  const icons: string[] = []

  for (const benefit of props.tech.provides) {
    if (benefit.type === 'buildings') {
      const effects = assetStore.getBuildingEffectsByCode(benefit.code) as any
      if (effects && Array.isArray(effects)) {
        for (const effect of effects) {
          if (effect.bonus > 0) {
            const type = CDN.normalize(effect.type)
            // Prevent duplicate icons
            if (!icons.includes(type)) {
              icons.push(type)
            }
          }
        }
      }
    } else if (benefit.type === 'ship_components') {
      const effects = assetStore.getShipComponentEffectsByCode(benefit.code) as any
      if (effects && Array.isArray(effects)) {
        for (const effect of effects) {
          if (effect.modifier > 0) {
            if (!['ship_mass', 'ship_production_cost', 'ore'].includes(effect.type)) {
              const type = CDN.normalize(effect.type)
              if (!icons.includes(type)) {
                icons.push(type)
              }
            }
          }
        }
      }
    }
  }

  return icons
})

// Get current alliance
const currentAlliance = computed(() => {
  return gameStore.player?.allianceId ? allianceStore.getAlliance(gameStore.player.allianceId) : null
})

const isAlreadyResearched = computed(() => {
  // Check if this prototype has already been researched by the alliance
  return currentAlliance.value?.prototypeIds?.includes(props.tech.id) || false
})

const isCurrentAllianceResearch = computed(() => {
  // Check if this tech is the current alliance research target
  return currentAlliance.value?.currentTechId === props.tech.id
})

onMounted(() => {
  top.value = 3.5 + (props.tech.treeRow - 1) * 13
  left.value = 3 + (props.tech.treeColumn - 1) * 30
})
</script>

<template>
  <StandardCard
    :title="name"
    :style="'top:' + top + 'rem;left:' + left + 'rem'"
    :class="{
      'tech ': true,
      'opacity-40': isAlreadyResearched,
      'shadow-even-md shadow-blue-600': isCurrentAllianceResearch,
      'hover:shadow-even-md hover:shadow-green-600': !isAlreadyResearched && !isCurrentAllianceResearch
    }"
  >
    <ModalTrigger type="allianceTech" :data="{ techId: props.tech.id }">
      <div class="flex">
        <img
          :src="CDN.techIcon(props.tech.code)"
          alt=""
          class="rounded shrink-0"
          style="width: 5rem !important; height: 5rem !important"
        />
        <div class="ps-2 flex flex-col justify-between w-full">
          <div class="flex justify-end">
            <template v-for="icon in provideIcons" :key="icon">
              <GenericIcon :type="icon" class="ml-2" />
            </template>
          </div>
          <div class="flex justify-end">
            <ActionButton
              v-if="!isAlreadyResearched && !isCurrentAllianceResearch"
              @click.stop="selectPrototype"
            >
              <GenericIconWithNumber
                :amount="props.tech.cost"
                type="blueprint"
                text-color="text-slate-300"
              />
            </ActionButton>
            <div v-else-if="isCurrentAllianceResearch" class="text-blue-400 text-sm">
              Current Prototype
            </div>
            <!-- Show nothing when already researched -->
          </div>
        </div>
      </div>
    </ModalTrigger>
  </StandardCard>
</template>

<style scoped>
.tech {
  position: absolute;
  width: 18rem;
}
</style> 